import React ,{ useEffect, useState} from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ToastDefault from "../../components/Toast";
import { useNavigate, Link } from "react-router-dom";
import { stringify } from "querystring";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import * as Yup from "yup";
import './login.css';
import { LoginAction } from "../../redux/actions/UserActions";

type SeoContent = {
  meta_title: string;
  meta_description?: string;
  // Add more fields as needed
};

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email address").required("Email is required"),
  password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
});

const Login: React.FC = () => {


    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const { status, data, error, loading } = useSelector((state: any) => state.loginState);

    const [soeContent, setSoeContent] = useState<SeoContent | null>(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState<{ email?: string; password?: string; api?: string }>({});
    const [loadingM, setLoadingM] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      setLoadingM(true);
      e.preventDefault();
      setErrors({});
      try {
        await LoginSchema.validate({ email, password }, { abortEarly: false });
        // Example: Replace with your Redux action or API call
        console.log(" Submitting : ", { email , password , user_type:"Manual" });
        await dispatch(LoginAction({ email, password , user_type:"Manual" }));
        // Simulate API delay
      } catch (err: any) {
        if (err.inner) {
            const formErrors: { email?: string; password?: string } = {};
            err.inner.forEach((validationErr: any) => {
              const key = validationErr.path as keyof typeof formErrors;
              formErrors[key] = validationErr.message;
            });
            setErrors(formErrors);
            setTimeout(() => {
              setLoadingM(false);
            }, 200);
        } else {
          setLoadingM(false);
          setErrors({ api: "Something went wrong. Please try again." });
        }
      }
    };

    useEffect(() => {

        if(status){
            dispatch(LoginAction('RESET'));
            toast.success(data?.message);
            localStorage.setItem("access-token", data?.session_token);
            localStorage.setItem("user-data", JSON.stringify(data?.data));
            setTimeout(() => {
              setLoadingM(false);
              navigate("/dashboard");
            }, 2000);
        }else{
          setLoadingM(false);
        }
        
        if(error) {
          dispatch(LoginAction('RESET'));
          setLoadingM(false);
        }

    }, [dispatch, status , error])

  return (
    <>
    <Helmet>
      <title>{`${soeContent?.meta_title || 'Login'}`}</title>
      <meta name="title" content={soeContent?.meta_title || 'E-learning Platform'} />
      <meta name="description" content={soeContent?.meta_description || 'Learn with curated e-learning resources'} />
      <meta name="keywords" content="e-learning, tech-cards, cheat-sheets, tutorials" />
    </Helmet>

      <Header />

        <div className="login-wrapper">
          <div className="login-card">
            <div className="image-section">
              <div className="image-content">
                <h2>Welcome Back!</h2>
                <p>Continue your learning journey with Uplatz. Access your courses, track your progress, and achieve your career goals.</p>
                <div className="mt-4">
                  <i className="bi bi-book fs-1"></i>
                </div>
              </div>
            </div>
            
            <div className="form-section">
              <div className="text-center mb-4">
                <i className="bi bi-person-circle text-primary fs-1"></i>
                <h3 className="fw-bold mt-3">Login to Your Account</h3>
                <p className="text-muted">Enter your credentials to access your dashboard</p>
              </div>

              <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              <div className="mb-3">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className={`form-control ${errors.password ? "is-invalid" : ""}`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                <div className="text-end mt-2">
                  <Link
                    to={"/forgot-password"}
                    className="text-decoration-none text-primary small"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>

              <div className="mb-3 form-check">
                <input type="checkbox" className="form-check-input" id="remember" />
                <label className="form-check-label">Remember me</label>
              </div>

              {errors.api && <div className="alert alert-danger">{errors.api}</div>}

              <div className="d-grid mb-3">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg fw-semibold"
                  disabled={loadingM}
                >
                  {loadingM ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Logging ...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-box-arrow-in-right me-2"></i>Login
                    </>
                  )}
                </button>
              </div>

              {/* <div className="divider">
                <span className="divider-text">OR</span>
              </div>

              <div className="social-login">
                <a href="#" className="social-btn google-btn" title="Login with Google">
                  <i className="bi bi-google"></i>
                </a>
                <a href="#" className="social-btn facebook-btn" title="Login with Facebook">
                  <i className="bi bi-facebook"></i>
                </a>
                <a href="#" className="social-btn linkedin-btn" title="Login with LinkedIn">
                  <i className="bi bi-linkedin"></i>
                </a>
              </div> */}

              <div className="text-center mt-4">
                <p className="mb-0">
                  Don't have an account? <Link to={"/registration"}  className="text-decoration-none text-primary" >Sign Up</Link>
                </p>
              </div>
            </form>
            </div>
          </div>
        </div>
        <ToastDefault/>
      <Footer />
    </>
  );
};

export default Login;
